import React, { useState, useEffect, useRef } from "react";
import { useMap } from "react-map-gl";
import { useMapboxDraw } from "../..";
import { useSelector, useDispatch } from "react-redux";
import { Tooltip } from "antd";
import { inputsActions } from "../../../Redux";
import * as turf from "@turf/turf";

const DrawMultiPolygon = () => {
  const dispatch = useDispatch();
  const { current: mapRef } = useMap();
  const map = mapRef?.getMap();
  const draw = useMapboxDraw();

  const is_drawing_multi = useSelector((state) => state.inputs.uiState.is_drawing_multi);
  const is_drawing_multi_ref = useRef(is_drawing_multi);
  const site_features = useSelector((state) => state.inputs.site_features);

  // Refs for drawing state
  const isDrawingBox = useRef(false);
  const startPoint = useRef(null);
  const boxLayerId = "selection-box";
  const boxSourceId = "selection-box-source";
  const handlersAttached = useRef(false);

  useEffect(() => {
    is_drawing_multi_ref.current = is_drawing_multi;
    console.log("is_drawing_multi changed to:", is_drawing_multi);
  }, [is_drawing_multi]);

  useEffect(() => {
    if (!map || !draw) return;

    // Add the selection box source and layer if they don't exist in the map yet
    if (!map.getSource(boxSourceId)) {
      map.addSource(boxSourceId, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });
    }

    if (!map.getLayer(boxLayerId)) {
      map.addLayer({
        id: boxLayerId,
        type: "fill",
        source: boxSourceId,
        paint: {
          "fill-color": "#3b82f6",
          "fill-opacity": 0.2,
        },
      });

      map.addLayer({
        id: `${boxLayerId}-outline`,
        type: "line",
        source: boxSourceId,
        paint: {
          "line-color": "#3b82f6",
          "line-width": 2,
          "line-dasharray": [2, 2],
        },
      });
    }

    const handleMouseDown = (e) => {
      if (!is_drawing_multi_ref.current) return;

      // Disable the map dragging so we don't drag the map when "drawing" a multi selection box
      map.dragPan.disable();
      map.boxZoom.disable();
      map.doubleClickZoom.disable();

      isDrawingBox.current = true;
      startPoint.current = [e.lngLat.lng, e.lngLat.lat];

      //   map.getCanvas().style.cursor = "crosshair";
    };

    const handleMouseMove = (e) => {
      if (!is_drawing_multi_ref.current || !isDrawingBox.current || !startPoint.current) return;

      const currentPoint = [e.lngLat.lng, e.lngLat.lat];

      // Create a rectangle from start to current point where the mouse currently is
      const bbox = [
        Math.min(startPoint.current[0], currentPoint[0]),
        Math.min(startPoint.current[1], currentPoint[1]),
        Math.max(startPoint.current[0], currentPoint[0]),
        Math.max(startPoint.current[1], currentPoint[1]),
      ];

      const boxPolygon = turf.bboxPolygon(bbox);

      // Update the box on the map
      const source = map.getSource(boxSourceId);
      if (source) {
        source.setData({
          type: "FeatureCollection",
          features: [boxPolygon],
        });
      }
    };

    const handleMouseUp = (e) => {
      if (!is_drawing_multi_ref.current || !isDrawingBox.current || !startPoint.current) return;

      const currentPoint = [e.lngLat.lng, e.lngLat.lat];

      // Create the bounding box that we check for intersections with the features
      const bbox = [
        Math.min(startPoint.current[0], currentPoint[0]),
        Math.min(startPoint.current[1], currentPoint[1]),
        Math.max(startPoint.current[0], currentPoint[0]),
        Math.max(startPoint.current[1], currentPoint[1]),
      ];

      const boxPolygon = turf.bboxPolygon(bbox);

      // Find all the features that intersect with the selection box that we just drew
      const selectedIds = [];
      Object.values(site_features).forEach((feature) => {
        try {
          // Check if feature intersects with or is contained in the selection box
          if (turf.booleanIntersects(feature, boxPolygon) || turf.booleanContains(boxPolygon, feature)) {
            selectedIds.push(feature.properties.index);
          }
        } catch (error) {
          console.warn("Error checking feature intersection:", error);
        }
      });

      // Update Redux with selected feature IDs [array]
      dispatch(inputsActions.update_input("selectedFeatureId", selectedIds));

      console.log("Selected feature IDs:", selectedIds);

      // Clear the box
      const source = map.getSource(boxSourceId);
      if (source) {
        source.setData({
          type: "FeatureCollection",
          features: [],
        });
      }

      // Reset the drawing state
      isDrawingBox.current = false;
      startPoint.current = null;
      map.getCanvas().style.cursor = "";

      // Re-enable the map interactions so we can drag, and zoom and stuff
      map.dragPan.enable();
      map.boxZoom.enable();
      map.doubleClickZoom.enable();

      // end drawing multi-select
      dispatch(inputsActions.update_ui_state("is_drawing_multi", false));
    };

    // this manages the drawing event listeners
    if (is_drawing_multi && !handlersAttached.current) {
      map.on("mousedown", handleMouseDown);
      map.on("mousemove", handleMouseMove);
      map.on("mouseup", handleMouseUp);
      handlersAttached.current = true;
    } else if (!is_drawing_multi && handlersAttached.current) {
      map.off("mousedown", handleMouseDown);
      map.off("mousemove", handleMouseMove);
      map.off("mouseup", handleMouseUp);
      handlersAttached.current = false;
      // this just ensures map interactions are re-enabled when exiting multi-select mode
      map.dragPan.enable();
      map.boxZoom.enable();
      map.doubleClickZoom.enable();
    }

    return () => {
      if (handlersAttached.current) {
        map.off("mousedown", handleMouseDown);
        map.off("mousemove", handleMouseMove);
        map.off("mouseup", handleMouseUp);
        handlersAttached.current = false;
        // Ensure map interactions are re-enabled on cleanup
        map.dragPan.enable();
        map.boxZoom.enable();
        map.doubleClickZoom.enable();
      }
    };
  }, [map, draw, is_drawing_multi, site_features, dispatch]);

  const handleToggleMultiSelect = () => {
    // Fire event for centralized handler in MapboxToolbar
    map.fire("toggle.multiselect");
  };

  return (
    <Tooltip title={Object.values(site_features).length === 0 ? "Please draw a polygon to use Multi-Select tool" : "Multi-Select Tool"} placement="bottom" mouseEnterDelay={0.25}>
      <button
        className={is_drawing_multi ? "button-active" : ""}
        onClick={handleToggleMultiSelect}
        disabled={Object.values(site_features).length === 0}
        style={Object.values(site_features).length === 0 ? { cursor: "not-allowed", opacity: 0.5 } : {}}
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8 0H18C19.1523 0 20 0.847715 20 2V12C20 13.1523 19.1523 14 18 14H14V18C14 19.1523 13.1523 20 12 20H2C0.847715 20 0 19.1523 0 18V8C0 6.84772 0.847715 6 2 6H6V2C6 0.847715 6.84772 0 8 0ZM6 8H2V18H12V14H8C6.84772 14 6 13.1523 6 12V8ZM8 2V12H18V2H8Z"
            fill="#333"
          />
        </svg>
      </button>
    </Tooltip>
  );
};

export { DrawMultiPolygon };
